<template>
    <div class="coach-mark-layer" :class="{ 'coach-mark-layer-blocking': isSpotlight }">
        <transition name="coach-mark-fade">
            <div v-if="isSpotlight" class="coach-mark-backdrop" :style="backdropStyle" @click="onBackdropClick" />
        </transition>
        <transition name="coach-mark-fade">
            <div v-if="isSpotlight" class="coach-mark-spotlight" :style="spotlightStyle" />
        </transition>
        <transition name="coach-mark">
            <div v-if="isVisible" class="coach-mark" :style="markStyle">
                <span class="coach-mark-arrow" :style="arrowStyle" />
                <span class="coach-mark-text">{{ text }}</span>
                <div class="coach-mark-actions">
                    <v-btn text small color="secondary" class="coach-mark-action" @click="dismiss">
                        {{ actionLabel }}
                    </v-btn>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: "CoachMark",
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        target: {
            default: null,
        },
        text: {
            type: String,
            default: '',
        },
        actionText: {
            type: String,
            default: '',
        },
        spotlight: {
            type: Boolean,
            default: false,
        },
        spotlightPadding: {
            type: Number,
            default: 6,
        },
        spotlightRadius: {
            type: Number,
            default: 999,
        },
        blur: {
            type: [String, Number],
            default: 0,
        },
        width: {
            type: Number,
            default: 260,
        },
        offset: {
            type: Number,
            default: 12,
        },
    },
    data() {
        return {
            ready: false,
            position: { top: 0, left: 0, width: 260, arrow: 0 },
            hole: { top: 0, left: 0, width: 0, height: 0 },
        }
    },
    computed: {
        isVisible(vm = this) {
            return vm.value && vm.ready
        },
        isSpotlight(vm = this) {
            return vm.isVisible && vm.spotlight
        },
        actionLabel(vm = this) {
            return vm.actionText || vm.$t('common.understood')
        },
        markStyle(vm = this) {
            return {
                top: `${vm.position.top}px`,
                left: `${vm.position.left}px`,
                width: `${vm.position.width}px`,
            }
        },
        arrowStyle(vm = this) {
            return { left: `${vm.position.arrow}px` }
        },
        backdropStyle(vm = this) {
            if (!vm.blur) return {}
            const radius = Math.max(vm.hole.width, vm.hole.height) / 2
            const x = vm.hole.left + (vm.hole.width / 2)
            const y = vm.hole.top + (vm.hole.height / 2)
            const mask = `radial-gradient(circle at ${x}px ${y}px, transparent ${radius}px, #000 ${radius + 1}px)`
            return {
                backdropFilter: `blur(${vm.blur}px)`,
                maskImage: mask,
            }
        },
        spotlightStyle(vm = this) {
            return {
                top: `${vm.hole.top}px`,
                left: `${vm.hole.left}px`,
                width: `${vm.hole.width}px`,
                height: `${vm.hole.height}px`,
                borderRadius: `${vm.spotlightRadius}px`,
            }
        },
    },
    mounted() {
        const vm = this
        document.body.appendChild(vm.$el)
        if (vm.value) vm.open()
    },
    beforeDestroy() {
        const vm = this
        vm.close()
        if (vm.$el.parentNode) vm.$el.parentNode.removeChild(vm.$el)
    },
    methods: {
        targetElement(vm = this) {
            if (!vm.target) return null
            if (typeof vm.target === 'string') return document.querySelector(vm.target)
            if (vm.target.$el) return vm.target.$el
            return vm.target
        },
        open() {
            const vm = this
            vm.$nextTick(() => {
                vm.updatePosition()
                window.addEventListener('resize', vm.updatePosition)
                window.addEventListener('scroll', vm.updatePosition, true)
                document.addEventListener('mousedown', vm.onOutsideClick, true)
                document.addEventListener('touchstart', vm.onOutsideClick, true)
                document.addEventListener('keydown', vm.onKeydown)
            })
        },
        close() {
            const vm = this
            vm.ready = false
            window.removeEventListener('resize', vm.updatePosition)
            window.removeEventListener('scroll', vm.updatePosition, true)
            document.removeEventListener('mousedown', vm.onOutsideClick, true)
            document.removeEventListener('touchstart', vm.onOutsideClick, true)
            document.removeEventListener('keydown', vm.onKeydown)
        },
        updatePosition() {
            const vm = this
            const el = vm.targetElement()
            const rect = el ? el.getBoundingClientRect() : null
            if (!rect || (!rect.width && !rect.height)) {
                vm.ready = false
                vm.$emit('input', false)
                return
            }
            const width = Math.min(vm.width, window.innerWidth - 24)
            const center = rect.left + (rect.width / 2)
            const left = Math.max(12, Math.min(center - (width / 2), window.innerWidth - width - 12))
            vm.position = {
                top: rect.bottom + vm.offset,
                left,
                width,
                arrow: center - left,
            }
            vm.hole = {
                top: rect.top - vm.spotlightPadding,
                left: rect.left - vm.spotlightPadding,
                width: rect.width + (vm.spotlightPadding * 2),
                height: rect.height + (vm.spotlightPadding * 2),
            }
            vm.ready = true
        },
        onOutsideClick(e) {
            const vm = this
            if (!vm.$el.contains(e.target)) vm.dismiss()
        },
        onBackdropClick(e) {
            const vm = this
            const el = vm.targetElement()
            const insideHole = e.clientX >= vm.hole.left && e.clientX <= vm.hole.left + vm.hole.width
                && e.clientY >= vm.hole.top && e.clientY <= vm.hole.top + vm.hole.height
            vm.dismiss()
            if (insideHole && el && typeof el.click === 'function') el.click()
        },
        onKeydown(e) {
            if (e.key === 'Escape') this.dismiss()
        },
        dismiss() {
            const vm = this
            if (!vm.value) return
            vm.$emit('input', false)
            vm.$emit('dismiss')
        },
    },
    watch: {
        value(val) {
            if (val) this.open()
            else this.close()
        },
        target() {
            if (this.value) this.updatePosition()
        },
        $route() {
            this.dismiss()
        },
    },
}
</script>

<style scoped>
.coach-mark-layer {
    position: fixed;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    z-index: 9;
}

.coach-mark-layer-blocking {
    width: 100%;
    height: 100%;
}

.coach-mark-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.coach-mark-spotlight {
    position: fixed;
    pointer-events: none;
    border: 2px solid var(--v-primary-base);
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.62);
}

.coach-mark {
    position: fixed;
    display: flex;
    flex-direction: column;
    padding: 14px 16px 8px;
    border-radius: 12px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
}

.coach-mark-arrow {
    position: absolute;
    top: -6px;
    width: 11px;
    height: 11px;
    background: var(--v-surface-base);
    border-left: 1px solid var(--v-outline-base);
    border-top: 1px solid var(--v-outline-base);
    border-radius: 2px 0 0 0;
    transform: translateX(-50%) rotate(45deg);
}

.coach-mark-text {
    font-size: 13px;
    line-height: 1.45;
    color: var(--v-txtPrimary-base);
}

.coach-mark-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 4px;
}

.coach-mark-action {
    letter-spacing: normal;
    text-transform: none;
    font-size: 13px;
    font-weight: 600;
}

.coach-mark-fade-enter-active,
.coach-mark-fade-leave-active {
    transition: opacity 0.2s ease;
}

.coach-mark-fade-enter,
.coach-mark-fade-leave-to {
    opacity: 0;
}

.coach-mark-enter-active,
.coach-mark-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
}

.coach-mark-enter,
.coach-mark-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
</style>
